import React, { useState, useEffect, useCallback } from 'react';
import {} from 'reactstrap';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import notify from '../../../utils/notify';
import '../../TenderOpening/components/TenderOpening.css';

import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import ListPager from '../../../Components/Common/ListPager';
import {
  EmptyState,
  SkeletonRows,
} from '../../../Components/Common/ListStates';
import RolePermissionAssignModal from './RolePermissionAssignModal';
import StatusBadge from '../../../Components/Common/StatusBadge';

const RolePermissionManagement = () => {
  const token = getAuthToken();

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [assignModal, setAssignModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchRoles = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const queryParams = {
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      };

      const qs = new URLSearchParams(queryParams);

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Name', debouncedSearchText.trim());
      }

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const rolesRes = await fetch(`${api.API_URL}/role?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!rolesRes.ok) {
        throw new Error(`HTTP error! status: ${rolesRes.status}`);
      }

      const rolesData = await rolesRes.json();
      setRoles(rolesData.data?.items || []);
      setTotalRows(rolesData.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load roles');
    } finally {
      setIsLoading(false);
    }
  }, [
    token,
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const openAssignModal = role => {
    setSelectedRole(role);
    setAssignModal(true);
  };

  const clearFilter = () => {
    setSearchText('');
    setDebouncedSearchText('');
    setCurrentPage(1);
  };

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: clearFilter,
    },
  ].filter(Boolean);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      id: 'name',
      name: 'Role Name',
      selector: row => row.name || row.id,
      sortable: true,
      cell: row => (
        <div className='d-flex align-items-center gap-2'>
          <span className='to-title' title={String(row.name || row.id)}>
            {row.name || row.id}
          </span>
          {row.name === 'Super Admin' && (
            <StatusBadge tone='warning' label='Bypass' />
          )}
        </div>
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '200px',
      center: true,
      cell: row => (
        <div className='d-flex flex-wrap gap-2 justify-content-end justify-content-md-center'>
          <ActionButton
            variant='secondary'
            size='sm'
            icon='ri-shield-keyhole-line'
            onClick={() => openAssignModal(row)}
          >
            Manage Permissions
          </ActionButton>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
        <div className='email-content minimal-border'>
          <div className='p-4'>
            <div className='to-hero mb-4'>
              <PageBreadcrumb fallback={{ label: 'Role Permission' }} />

              <div className='d-flex align-items-center gap-3 flex-wrap'>
                <span className='to-hero-icon'>
                  <i className='ri-shield-keyhole-line' aria-hidden='true' />
                </span>
                <div className='flex-grow-1'>
                  <h4 className='to-hero-title'>Role Permission</h4>
                  <p className='to-hero-subtitle'>
                    Manage the permissions granted to each role.
                  </p>
                </div>
                <div className='d-flex gap-2 flex-wrap'>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={() => fetchRoles()}
                  >
                    <i className='ri-refresh-line' aria-hidden='true' />
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className='to-panel'>
              <div className='to-toolbar'>
                <ListSearch
                  value={searchText}
                  onChange={handleSearch}
                  placeholder='Search role name...'
                  ariaLabel='Search role name'
                />
                <ClearFiltersButton
                  count={filterChips.length}
                  onClick={clearFilter}
                />
                <span className='to-toolbar-meta'>
                  {isLoading
                    ? 'Loading...'
                    : `Showing ${startEntry}–${endEntry} of ${totalRows} roles`}
                </span>
              </div>

              <ActiveFilterChips chips={filterChips} />

              <div className='to-table-wrap'>
                <DataTable
                  columns={columns}
                  data={roles}
                  onSort={(column, dir) => {
                    setSortField(column.id);
                    setSortDirection(dir);
                    setCurrentPage(1);
                  }}
                  sortServer
                  highlightOnHover
                  persistTableHead
                  defaultSortFieldId='name'
                  defaultSortAsc
                  progressPending={isLoading}
                  progressComponent={<SkeletonRows />}
                  noDataComponent={
                    <EmptyState
                      title='No role found'
                      emptyHint='No roles exist yet, so there is nothing to grant permissions to. Roles are created in User Management > Roles.'
                      filteredHint={`No role matches the search "${searchText}". Check the spelling or clear the search.`}
                      hasFilters={filterChips.length > 0}
                      onClear={clearFilter}
                    />
                  }
                  customStyles={{
                    headCells: {
                      style: { paddingLeft: '12px', paddingRight: '12px' },
                    },
                    cells: {
                      style: {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        wordBreak: 'break-word',
                      },
                    },
                    table: { style: { width: '100%' } },
                  }}
                />
              </div>

              <ListPager
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={totalRows}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePerRowsChange}
              />
            </div>
          </div>
        </div>
      </div>

      <RolePermissionAssignModal
        isOpen={assignModal}
        toggle={() => setAssignModal(false)}
        token={token}
        selectedRole={selectedRole}
      />
    </React.Fragment>
  );
};

export default RolePermissionManagement;
